import $ from 'jquery'
import Backbone from 'backbone'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import Bootslide from 'bootslide'
import icon from 'scoreboard/components/icon'
import constants from 'scoreboard/components/constants'
import ExpandCollapse from 'scoreboard/components/object-selector-expand-collapse'
import getTiledBootslideMenuWidth from 'scoreboard/components/bootslide/get-tiled-bootslide-menu-width'
import menuIconOption from 'scoreboard/components/bootslide/menu-icon-option'
import Tooltip from 'scoreboard/components/tooltip'

var nextId = 1

// Usage
/*
 * There are three parts to configuring the a bootslide menu.
 *
 * (1) The instance properties of the bootslide menu (BootslideMenu)
 * (2) The config options given to the entire menu (BootslideMenuConfig)
 * (3) The config options for each menu item. (BootslideMenuItem)
 */

// Entire Menu Example
/*
 const MyBootslideMenu = BootslideMenu.extend({

	initialize: function (options = {}) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.options = options

		this.allowDynamicWidth = true // Optional
	},

	// Menu is how you provide the list of options
	menu: function () {
		let topLevel = []
		this.addTopLevelOptions(topLevel)
		return {
			label: i18n.get('common.edit2', this.typeLabel()),
			target: topLevel,
			// Optional
			width: BOOTSLIDE_MENU_WIDTH
		}
	}

	// Optional
	resizeOnSide(target) {}
 */

// Menu Option Examples
/*
	An option has a `label` and a `target` at a minimum, see BootslideMenuItem for all options.

	// Tile Options (icon + label); see it in action: Chart lines and bands analytic scope option
	createScopeOption: function() {
		return {
			label: 'Scope',
			layout: 'tiles',
			target: [
				{content: menuIconOption('entire-chart', 'charts.entireChart')},
				{content: menuIconOption('per-country', 'charts.perCountry')},
				{content: menuIconOption('per-field', 'charts.perField')}
			]
		}
	}

	// Target Option (this is a menu option that opens a new dialog)
	return BootslideMenu.targetOption('report.designer.setDataSource', viewCreator, results => {
		// optional callback function that is invoked when the view emits 'done'
		// All arguments are passed through (e.g. `results` in this example)
		this.updateDataSource(results)
		this.trigger('done')
	})

	// Icon Options (tiles) that do some action and close the menu but don't launch a new view
	// see lines-and-bands.js#EditMenu for the full example
	const onClick = scope => this.feature.set('scope', scope)
	return {
		label: 'charts.scope',
		layout: 'tiles',
		target: [
			this.iconActionOption('per-series', 'charts.perSeries', onClick),
			...
		]
	}

	// contentEndpoint is used when clicking on a menu item should result in
	// another level, but not the standard generic Bootslide menu:
	return {
		label: 'chart.someCoolMenu',
		contentEndpoint: () => {
			return new SomeMenuView({}).render().el
		}
	}
 */

// Types
/**
 * @typedef BootslideMenu
 *
 * @property {Boolean} [allowDynamicWidth=false]
 * @property {Function} [resizeOnSlide] A function called on slide which can be
 * used to size the current target
 */

/**
 * @typedef BootslideMenuConfig
 *
 * @property {Number} [width] Sets the width for the entire menu.
 * Alternatively you can set allowDynamicWidth on the menu itself.
 */

/**
 * @typedef BootslideMenuItem
 *
 * @property {String} [id]
 *
 * @property {String} label Can be an i18n code or already translated.
 *
 * @property {BootslideMenuItem[]} [target] the targets for the next menu level
 *
 * @property {String} [content] the content of an item's row in the menu, as an
 * HTML string
 *
 * @property {View} [contentView] same as the 'content' option, but can be a
 * Backbone View instance, or a View constructor
 *
 * @property {Function} [contentEndpoint] a function that will render the
 * submenu that appears after clicking on this menu item. This is used when
 * the next level of the menu should not be another list of menu items, but
 * an arbitrary view. For example, this menu item might be Dataset, and the
 * contentEndpoint is a function that returns a dataset field selector
 *
 * @property {String} [layout] Options:
 * 	'tiles' : this will add a `bootslide-tiles` class to the <ul>, which will
 * 	render the menu as a grid of tiles (instead of a vertical list)
 *
 * @property {String[]} [classNames] Any additional classes to add to the menu item
 * 	(e.g. ['is-selected'])
 */

/**
 *
 * @type {BootslideMenu}
 */
var BootslideMenu = Backbone.View.extend({

	/**
	 *
	 * @param {BootslideMenuConfig} options
	 */
	initialize: function (options = {}) {
		this.options = options
		this.canvas = options.canvas // TODO: this probably belongs in a subclass for dashboards
	},

	/**
	 *
	 * @param {Boolean} [options.replace=false] if true, the root element will be cleared of any
	 * 		existing content before attaching the new menu elements
	 */
	render: function (options = {}) {
		var config = this
		this.childViews = []

		function toBootslideMenu (item) {
			if (item.contentView) {
				item.content = function () {
					let v
					if (typeof item.contentView === 'function') {
						// It's a View constructor; create an instance of it
						v = new item.contentView({
							model: item.model || config.model,
							widgetUpdater: config.options.widgetUpdater
						})
					} else {
						// It's already an instance of a View; just need to
						// render it
						v = item.contentView
					}

					config.childViews.push(v)

					config.listenTo(v, 'done', config.trigger.bind(config, 'done'))

					return v.render().el
				}
			}

			item.label = item.label ? i18n.get(item.label) : ''

			if (!item.id) {
				item.id = 'menu-item-' + nextId++
			}

			if (_.isArray(item.target)) {
				_.each(item.target, toBootslideMenu)
			}

			// #43710: Bootslide must not be allowed to try and create its own ID,
			// which it will do, from the label, if an ID is not provided. This is a
			// problem because it will use a regex to clean up the label, for use as an
			// attribute, and that expression does not recognize Arabic characters
			// as word characters, and will strip them out entirely.
			_(item.categories || [])
				.flatMap(it => it.target)
				.forEach((it, idx) => it.id = it.id || `sub-menu-${idx+1}`)
		}

		var menu = _.result(this, 'menu')
		toBootslideMenu(menu)

		this.bootslide = new Bootslide(menu, {
			last: '',
			back: icon('arrow-back', 'icon-arrow-back'),
			next: icon('arrow-forward', 'icon-arrow-forward'),
			defaultTarget: function (e) {
				e.stopPropagation()
				e.preventDefault()

				// We're letting the child view handle clicks. But if the click hits
				// bootslide's li or li > a, we want to kill that event and manually
				// trigger a click on the child view so it can do its thing
				var target = $(e.target)
				if (isEndpoint(target)) {
					target.children().eq(0).click()
				}
			},
			width: this.options.width || constants.DEFAULT_BOOTSLIDE_MENU_WIDTH
		})

		this.bootslide.on('slide', (index, target) => {
			// Keep track of the current menu target, and notify its views when
			// the menu slides away from them
			if (this.currentTarget) {
				let previousViews = this.childViews
					.filter(v => this.currentTarget.has(v.el).length > 0)

				if (previousViews) {
					previousViews.forEach(v => v.trigger('slide:away'))
				}
			}

			// if `allowDynamicWidth = true`, make sure the children items aren't overflowing
			if (config.allowDynamicWidth) {
				this.setDynamicWidth({ config, target })
			}

			this.currentTarget = target
			_.invoke(config, 'resizeOnSlide', this.currentTarget)
		})

		const el = this.$el
		if (options.replace) {
			el.empty()
		}
		el.append(this.bootslide.render())

		return this
	},

	setDynamicWidth: function({ config, target, updateMenuWidth }) {
		target.css('width', '') // temporarily remove the width, so we can grab the width needed to avoid overflow
		// Issue #56305
		// If target contains child .color-picker-tooltip, set width to
		// color picker width.
		// else set to default
		let width = target.find('.color-picker-tooltip').length > 0
			? constants.BOOTSLIDE_MENU_COLOR_PICKER_WIDTH
    		: config?.width || constants.DEFAULT_BOOTSLIDE_MENU_WIDTH
		let minWidth = target.width()
		target.css({ minWidth })
		// If the minWidth is greater than the default width, don't set the
		// the default width
		if (width >= minWidth) {
			target.css({ width })
		} else if (updateMenuWidth) {
			this.bootslide.setWidth(minWidth)
		}

		// Have bootslide reset its margins. What this does is make sure all the bootslide
		// slides update their translate property to reflect the sum of all previous slides.
		// If the target bootslide here changes in width, this will prevent #42856
		this.bootslide.resetMargins()
	},

	back: function () {
		this.bootslide.back()
	},

	setTreeWidths({el, gardeners, types}) {
		this.expandCollapse = new ExpandCollapse({
			el,
			gardeners,
			types,
			unitType: 'px',
			hasContainer: true
		}).render()

		this.expandCollapse.on('updateWidth', menuWidth => this.setWidth({ menuOptionEl: el, menuWidth }))
	},

	setWidth: function ({ menuOptionEl, menuWidth }) {
		// Sets the actual menu width
		// #22409 but only if the tree is still the current menu item
		if (menuOptionEl.closest('.bootslide-section').hasClass('bootslide-current')) {
			this.setBootslideWidth(menuWidth)
		}
		// Sets the inside containers width
		menuOptionEl.css('width', menuWidth + 'px')
		// make sure all the bootslide slides update their translate properties
		this.bootslide.resetMargins()
	},

	/**
	 * Sets the bootslide width with or without transitions enabled
	 * @param {Number} width The width to resize to
	 * @param {Boolean} [noTransition] Whether or not to have transitions while resizing
	 */
	setBootslideWidth(width, noTransition) {
		noTransition && this.bootslide.el.addClass('no-transition')
		this.bootslide.setWidth(width)
		// Without pushing this out a bit, .no-transition gets nixed too soon and
		// transitions persist.
		setTimeout(() => this.bootslide.el.removeClass('no-transition'))
	},

	// Apply this method to briefly fade out/in the menu to show the selection
	// that would otherwise be hidden behind the menu. (i.e. adding widgets)
	// Issue #24271
	previewSelection: function() {
		let tooltip = this.$el.closest('.has-bootslide-menu')
		tooltip.addClass('is-previewing-selection')
		this.trigger('preview')
		setTimeout(() => {
			tooltip.removeClass('is-previewing-selection')
			this.trigger('endPreview')
		}, constants.PREVIEW_BOOTSLIDE_SELECTION_DELAY)
	},

	/**
	 * When a slide contains bootslide-tiles size that slide separately since the number of tiles present can vary.
	 * This function is triggered on initial render of the bootslide as well as upon each slide.
	 * @param {HTMLElement} target the dom element for the slide we're about to switch to / render
	 */
	resizeOnSlide(target) {
		let hasBootslideTiles = target.find('.bootslide-tiles').length > 0
		// If there aren't any bootslide tiles in this menu slide, there's no resizing
		// to be done because it's just a regular menu.
		if (!hasBootslideTiles && !this.setMaxHeight) {
			return
		}

		// Set the height/width based on how much space there is and how many targets there
		// are in the menu.
		let header = target.find('.bootslide-header').height()
		let menuSpaceHeight = this._getMenuSpace().height
		let maxHeight = menuSpaceHeight - header - constants.STANDARD_MARGIN*2

		target.find('.bootslide-container-row').css({ maxHeight })

		if (hasBootslideTiles) {
			let width =  getTiledBootslideMenuWidth(this.options.addButton, target)
			target.css({ width })
		}
	},

	/**
	 * Returns the available space for the bootslide to take up. This can be set to a manual
	 * constraint (via this.options.menuSpace, ex. dashboard-toolbar), but if that's not set,
	 * assume the max height/width possible is the window size.
	 */
	_getMenuSpace() {
		if (this.options.menuSpace) {
			return this.options.menuSpace
		} else {
			return {
				height: window.innerHeight,
				width: window.innerWidth
			}
		}
	},

	// Option Factory Functions
	/**
	 * A boot slide menu icon (tile) that takes some action when clicked.
	 *
	 * @param {String} options.value The option value and the only argument passed to `actionFn`
	 * @param {String} options.icon The icon value if it differs from `value`
	 * @param {String} options.label The label for the icon tile
	 * @param {Function} options.actionFn Invoked when the menu option is selected/clicked.
	 * @param {boolean} options.selected Pass true to apply selected styling to the option
	 * @param {String} [options.disabledMessage] If provided the option will be disabled
	 * and when clicked on this message will be shown in a tooltip
	 */
	iconActionOption: function({value, icon, label, actionFn, selected, disabledMessage}) {
		icon = icon || value
		let classNames = []
		if (selected) {
			classNames.push('is-selected')
		}
		if (disabledMessage) {
			classNames.push('is-disabled')
		}
		return {
			content: menuIconOption(icon, label),
			target: (e) => {
				e.preventDefault()
				e.stopPropagation()
				_.invoke(this.disabledOptionTooltip, 'remove')
				if (disabledMessage) {
					this.disabledOptionTooltip = new Tooltip({
						attachTo: e.target,
						content: disabledMessage,
						placement: 'above',
						arrow: 'center'
					}).render()
				} else {
					actionFn(value)
					this.trigger('done')
				}
			},
			classNames
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)

		this.childViews && _.each(this.childViews, function (v) {
			v.remove()
		})
		_.invokeMap([this.expandCollapse, this.disabledOptionTooltip], 'remove')
	}
})

function isEndpoint (target) {
	if (target.hasClass('bootslide-endpoint')) {
		return true
	}
	var parent = target.parent()
	if (parent && parent.hasClass('bootslide-endpoint')) {
		return true
	}
	return false
}

import onOffToggle from 'scoreboard/components/on-off-toggle.hbs'

var ToggleProperty = Backbone.View.extend({
	className: 'config-toggle',

	events: {
		'click': 'toggle'
	},

	initialize: function (options = {}) {
		this.options = options
		this.listenTo(this.model, 'change:' + this.property.name, this._onChange.bind(this))
	},

	render: function () {
		this.$el.html(i18n.get(this.property.label))
			.addClass(this.property.classNames)
			.append(onOffToggle({
				name: this.property.name,
				checked: this.val()
			}))
		this.$('.on-off-toggle').addClass('toggle-mini')

		this.$('input').prop('disabled', !!this.property.disabled)
		return this
	},

	val: function () {
		return this.model.get(this.property.name)
	},

	toggle: function (e) {
		e.preventDefault()

		if(!this.$('input').prop('disabled')) {
			this.updateModel(!this.val())
		}
	},

	updateModel: function (newVal) {
		this.model.set(this.property.name, newVal)
	},

	_onChange: function () {
		this.$('input').prop('checked', this.val())
	}
})

const WidgetToggleProperty = ToggleProperty.extend({
	updateModel: function (newVal) {
		this.options.widgetUpdater.setProperty(this.property.name, newVal)
	}
})

var MultipleChoiceOption = Backbone.View.extend({
	className: 'config-checkmark',

	events: {
		'click': 'select'
	},

	property: null,
	value: null,
	label: null,

	initialize: function (options = {}) {
		this.options = options
		this.listenTo(this.model, 'change:' + this.property, this.render.bind(this))
	},

	render: function () {
		if (this.label instanceof Element) {
			this.$el.empty().append(this.label)
		} else {
			this.$el.text(this.label)
		}

		this.$el
			.toggleClass('is-selected', this.selected())
			.toggleClass('is-disabled', this.disabled == true)

		if (this.selected()) {
			this.$el.append(icon('approved', 'check-icon'))
		}


		return this
	},

	selected: function () {
		return this.model.get(this.property) == this.value
	},

	select: function (e) {
		e.preventDefault()

		if (this.disabled) {
			return
		}

		this.updateModel(this.value)
	},

	updateModel(value) {
		this.model.set(this.property, value)
	}

})

const WidgetMultipleChoiceOption = MultipleChoiceOption.extend({
	updateModel: function (value) {
		this.options.widgetUpdater.setProperty(this.property, value)
	}
})

var MultipleChoiceMenu = Backbone.View.extend({
	property: null,

	initialize: function (options) {
		this.listenTo(this.model, 'change:' + this.property, this.render.bind(this))
	},

	render: function () {
		this.$el.html(_.result(this, 'label'))
		return this
	}
})

/**
 * Returns html for a config bootslide menu item given an icon and the menu label
 * @param {String} labelIcon the icon key for the icon which should be rendered
 * @param {String} label the label key which should be rendered
 */
function configIconLabel(labelIcon, label) {
	return icon(labelIcon, 'option-icon') +
	`<h3 class="option-label">${i18n.get(label)}</h3>`
}

var DeleteNode = Backbone.View.extend({
	className: 'config-delete',

	events: {
		'click': 'delete'
	},

	render: function () {
		this.$el.html(configIconLabel('delete', 'common.delete'))
		return this
	},

	delete: function (e) {
		e.stopPropagation()
		e.preventDefault()

		this.doDelete()
	},

	doDelete: function () {
		this.model.trigger('transaction', this.model.destroy.bind(this.model))
	}
})

function deleteTarget () {
	return {
		label: 'common.delete',
		contentView: DeleteNode
	}
}

/**
 * Creates a target function that takes care of all shared concerns but lets
 * the caller provide the view and do any custom logic.
 *
 * @param {Function<View>} viewCreator this is required
 * @param {Function>} [onDoneFn] invoked when the view triggers 'done', optional
 */
function targetDialog(viewCreator, onDoneFn) {
	return e => {
		e.preventDefault()
		let view = viewCreator()
		appDisplay.dialog(view)
		if (onDoneFn) {
			view.once('done', onDoneFn)
		}
	}
}

/**
 * A bootslide menu item target that launches a new view in a dialog.
 * @param {String} label
 * @param {Function<View>} viewCreator this is required
 * @param {Function} [onDoneFn] invoked when the view triggers 'done', optional
 */
function targetOption(label, viewCreator, onDoneFn) {
	return {
		label,
		target: targetDialog(viewCreator, onDoneFn)
	}
}

_.extend(BootslideMenu, {
	ToggleProperty,
	WidgetToggleProperty,
	DeleteNode,
	deleteTarget,
	targetOption,
	MultipleChoiceOption,
	WidgetMultipleChoiceOption,
	MultipleChoiceMenu,
	configIconLabel
})

export default BootslideMenu
BootslideMenu.__filename__ = 'components/bootslide/bootslide-menu.js'
